import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUserAttributes } from '@features/auth/slice';

/**
 * Returns a predicate answering "may the logged-in user read this merchant's
 * data?" — a client-side mirror of the platform's server-side rule
 * (`UserAccessInfo.HasAccessToMerchant`):
 *
 *   isAdmin || (entity is merchant && entityId === merchantId)
 *           || associatedIds.includes(merchantId)
 *
 * **This is not a security boundary.** The server enforces the real check on
 * every request; this only lets the UI avoid issuing a call it knows will be
 * refused, and decide whether a capability is worth offering. Never use it as
 * the sole gate on anything sensitive.
 *
 * The merchant relationship is recorded in one direction only — a parent
 * merchant's `associatedIds` lists its subaccounts, but a subaccount knows
 * nothing of its parent — so a subaccount user reading its parent is denied
 * here exactly as it would be server-side.
 *
 * Entity handling, and why each branch errs the way it does:
 * - No entity at all (admins, super admins, service callers) → allowed. They
 *   carry no id to compare and the server treats them as admin.
 * - A merchant entity → the id must match `entityId` or `associatedIds`.
 * - Any other entity (vendor, facility) → denied. Such users are not
 *   merchant-scoped, and denying is the safe direction: callers hide a
 *   capability rather than fire a doomed request. An admin that unexpectedly
 *   carries an entity lands here too and simply sees the capability hidden.
 *
 * `associatedIds` is a snapshot taken at login, so a merchant association made
 * mid-session is invisible until the user signs in again. That also fails
 * closed — the server would reject the call anyway — so the worst case is a
 * capability staying hidden until re-login, never a rejected request.
 */
export function useCanAccessMerchant(): (merchantId?: string) => boolean {
  const attrs = useSelector(selectUserAttributes);

  return useCallback(
    (merchantId?: string): boolean => {
      if (!merchantId) return false;
      const entity = attrs.entity ?? [];
      if (entity.length === 0) return true;
      if (!entity.some((v) => v?.toLowerCase() === 'merchant')) return false;
      const own = attrs.entityId ?? [];
      const associated = attrs.associatedIds ?? [];
      return own.includes(merchantId) || associated.includes(merchantId);
    },
    [attrs],
  );
}
